import isEmptyObject from '../../src/helpers/is-empty-object';
import { VISA } from '../constants/partnering-networks';
import collapsedLogger from './collapsed-logger';
import getNetworkInstance from './get-network-instance';
import getShippingPreference from './get-shipping-preference';
import getTransactionAmountDetails from './get-transaction-amount-details';
import isVInitReinvokedForSameMerchant from './is-vinit-reinvoked-for-same-merchant';
import OrchCustomData from './orch-custom-data';

/**
 * If VISA-SDK script exists in DOM, on subsequent vinit invocations, use this utility to update OrchcustomData currVInitRequest
 * for these 6 vInit overrides: total, subtotal, currencycode, buttonAction, collectShipping and  acceptedregions if updated
 * Also update dpaTransactionOptions for all networks
 * @param {Object} configs : merchant SDK latest configs
 * @return {Object}
 */
const pushUpdatedVInit = configs => {
  const configsVInit = configs?.vInitRequest;
  let isMultiVInitUpdated = false;
  const orchCustomDataVInitRequest = OrchCustomData.currVInitConfigs;
  if (
    isVInitReinvokedForSameMerchant(configs, OrchCustomData) &&
    !isEmptyObject(configsVInit) &&
    !OrchCustomData.isGetSRCProfileInvoked &&
    !OrchCustomData.isCheckoutInvoked
  ) {
    //update OrchCustomDate with multi-vinit overrides
    if (configsVInit.hasOwnProperty('paymentRequest')) {
      const orchCustomDataPaymentRequest = orchCustomDataVInitRequest.paymentRequest;
      if (configsVInit.paymentRequest.hasOwnProperty('total')) {
        orchCustomDataPaymentRequest.total = configsVInit?.paymentRequest?.total;
      }
      if (configsVInit.paymentRequest.hasOwnProperty('subtotal')) {
        orchCustomDataPaymentRequest.subtotal = configsVInit?.paymentRequest?.subtotal;
      }
      if (configsVInit.paymentRequest.hasOwnProperty('currencyCode')) {
        orchCustomDataPaymentRequest.currencyCode = configsVInit?.paymentRequest?.currencyCode;
      }
    }

    if (configsVInit.hasOwnProperty('review')) {
      if (
        isEmptyObject(orchCustomDataVInitRequest.review) ||
        !orchCustomDataVInitRequest.hasOwnProperty('review')
      ) {
        orchCustomDataVInitRequest.review = { buttonAction: '' };
      }
      orchCustomDataVInitRequest.review.buttonAction = configsVInit?.review?.buttonAction;
    }

    if (configsVInit.hasOwnProperty('shipping')) {
      //check if shipping is empty obj or key exists on orchCustomData??
      orchCustomDataVInitRequest.shipping.collectShipping = configsVInit?.shipping?.collectShipping;

      if (
        isEmptyObject(orchCustomDataVInitRequest.intialVinit) ||
        !orchCustomDataVInitRequest.hasOwnProperty('intialVinit')
      ) {
        orchCustomDataVInitRequest.intialVinit = {
          shipping: {}
        };

        orchCustomDataVInitRequest.intialVinit.shipping.collectShipping =
          configsVInit?.shipping?.collectShipping;
      } else {
        if (
          isEmptyObject(orchCustomDataVInitRequest.intialVinit.shipping) ||
          !orchCustomDataVInitRequest.intialVinit.hasOwnProperty('shipping')
        ) {
          orchCustomDataVInitRequest.intialVinit.shipping = {};
        }
        orchCustomDataVInitRequest.intialVinit.shipping.collectShipping =
          configsVInit?.shipping?.collectShipping;
      }

      orchCustomDataVInitRequest.shipping.acceptedRegions = configsVInit?.shipping?.acceptedRegions;

      orchCustomDataVInitRequest.intialVinit.shipping.acceptedRegions =
        configsVInit?.shipping?.acceptedRegions;
    }

    // update dpaTransactionOptions for all SRC networks
    for (const network in OrchCustomData.initData) {
      if (network !== VISA) {
        const transactionAmountDetails = getTransactionAmountDetails(orchCustomDataVInitRequest);
        const dpaTransactionOptions = OrchCustomData.initData[network].dpaTransactionOptions;
        dpaTransactionOptions.transactionAmount = transactionAmountDetails.transactionAmount;

        dpaTransactionOptions.reviewAction = configsVInit?.review?.buttonAction ?? 'CONTINUE';

        dpaTransactionOptions.dpaShippingPreference = getShippingPreference(configsVInit, network);

        dpaTransactionOptions.dpaAcceptedShippingCountries =
          configsVInit?.shipping?.acceptedRegions;
      }
    }

    const vco = getNetworkInstance(OrchCustomData, VISA);

    // update vsb flags with latest based on if merchant called V.initializeVsb or V.init
    orchCustomDataVInitRequest.isVsb = configs?.isVsb ?? false;
    orchCustomDataVInitRequest.isVsbButtonless = configs?.isVsbButtonless ?? false;
    if (OrchCustomData.initData.VISA) {
      OrchCustomData.initData.VISA.isVsb = configs?.isVsb ?? false;
      OrchCustomData.initData.VISA.isVsbButtonless = configs?.isVsbButtonless ?? false;
    }
    if (configs?.merchantIntegrationType) {
      OrchCustomData.initData.VISA.merchantIntegrationType = configs?.merchantIntegrationType;
    }
    //update system-sdk state
    vco && vco.pushUpdatedVinit(orchCustomDataVInitRequest);
    isMultiVInitUpdated = true;

    //Cal logging for updated Vinit for multi-vinit overrides
    collapsedLogger(
      'Finally ACCEPTED multi-vinit:: currVInitConfigs:::: ---> ',
      OrchCustomData?.currVInitConfigs ?? null
    );
    collapsedLogger('Finally multi-vinit updated:: OrchCustomData:::: ---> ', OrchCustomData);
    vco &&
      vco.pushEvents({
        E: `ORCH_INIT_${VISA}_UPDATED_MULTI_VINIT_CONFIGS`,
        ERRCODE: null,
        ERRMSG: null,
        LL: 'INFO',
        checkout_step: `ORCH_INIT_${VISA}_UPDATED_MULTI_VINIT_CONFIGS`,
        srcInitiatorId: OrchCustomData.srcInitiatorId[VISA]
      });
  } else {
    isMultiVInitUpdated = false;

    //Cal logging for REJECTED multi-vinit for SLA check failure
    const vco = getNetworkInstance(OrchCustomData, VISA);

    collapsedLogger(
      'Finally REJECTED multi-vinit configs DUE TO SLA CHECK:: vInitRequest:::: ---> ',
      configsVInit ?? null
    );
    collapsedLogger(
      'Finally updated OrchCustomData after multi-vinit Rejected due to missing SLA:: OrchCustomData:::: ---> ',
      OrchCustomData
    );

    vco &&
      vco.pushEvents({
        E: `ORCH_INIT_${VISA}_UPDATED_MULTI_VINIT_REJECTED_SLA`,
        ERRCODE: null,
        ERRMSG: null,
        LL: 'INFO',
        checkout_step: `ORCH_INIT_${VISA}_UPDATED_MULTI_VINIT_REJECTED_SLA`,
        srcInitiatorId: OrchCustomData.srcInitiatorId[VISA]
      });
  }

  return {
    isMultiVInitUpdated
  };
};

export default pushUpdatedVInit;
